// src/app/services/payment-link.service.ts

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DefaultResponseDTO } from 'src/app/dto/default/default-response.dto';
import { environment } from 'src/environments/environment';

export type BillingData = {
  link: string;
  status: string;
  billingId: string;
  billingType: 'PIX' | 'CREDIT_CARD' | 'WALLET';
  addressId: number | null;
  amount?: number;
};

@Injectable({
  providedIn: 'root',
})
export class PaymentLinkService {
  private readonly baseUrl = `${environment.api}/payment/billing-link`;
  private readonly baseUrlV2 = `${environment.api_v2}/v2/payment/generate-billing-link`;

  constructor(private readonly http: HttpClient) {}

  generateIndicationPaymentLink(body: {
    indicationId: number;
    planConsultationId: number;
    customer: {
      accountId: number;
      name: string;
      document: string;
      email: string;
    };
    billing: {
      description: string;
      type: 'PIX' | 'CREDIT_CARD';
      addressId?: number;
    };
  }) {
    return this.http.post<{
      isSuccess: boolean;
      redirectReschedule: boolean;
      link: string;
    }>(`${this.baseUrlV2}/indication`, body);
  }

  getBillingByIndicationId(indicationId: number) {
    return this.http
      .get<DefaultResponseDTO<BillingData>>(`${this.baseUrl}/indication/${indicationId}`)
      .pipe(map((response) => response.data));
  }

  generateDepositWalletBillingLink(body: {
    customer: {
      accountId: number;
      name: string;
      document: string;
      email: string;
    };
    billing: { amount: number; description: string; dueDate?: string };
  }) {
    return this.http.post<{ link: string }>(`${this.baseUrl}/wallet/deposit`, body);
  }

  updateBillingType(indicationId: number, billingType: 'PIX' | 'CREDIT_CARD' | 'WALLET') {
    return this.http.post<{ link: string }>(
      `${this.baseUrl}/indication/${indicationId}/update-billing-type`,
      { billingType }
    );
  }

  payIndicationWithWallet(body: {
    indicationId: number;
    addressId: number;
    planConsultationId: number;
  }) {
    return this.http.post<void>(`${this.baseUrlV2}/indication/pay-with-wallet`, body);
  }

  payIndication(indicationId: number, body: { method: 'CARD' | 'PIX' | 'WALLET'; addressId: number }) {
    return this.http.post<{ link?: string }>(
      `${this.baseUrlV2}/indication/${indicationId}`,
      body
    );
  }

  expireBilling(indicationId: number) {
    return this.http.post<void>(`${this.baseUrl}/indication/${indicationId}/expire`, {});
  }

  isAmountInvalid(amount: any): boolean {
    if (!amount) return true;
    const amountNumber = parseFloat(amount.replace(/\.(?=\d{3})/g, '').replace(',', '.'));
    if (isNaN(amountNumber) || amountNumber < 100) return true;
    return false;
  }

  updateBillingAddress(indicationId: number, newAddressId: number): Observable<{ link: string }> {
    return this.http.post<{ link: string }>(
      `${this.baseUrl}/indication/${indicationId}/update-address`,
      { addressId: newAddressId }
    );
  }
}
